import axios from 'axios';

// Set the base URL for your API
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';

// Create an Axios instance for API requests that require form data (e.g., file uploads)
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'multipart/form-data',
    },
});

// Create a standard Axios instance for JSON requests
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Create a new recipe
 * @param {FormData} data - FormData object containing recipe fields and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Created recipe data
 */
export const createRecipe = async (data, token) => {
    try {
        const response = await apiClient.post('/recipes', data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error creating recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update an existing recipe
 * @param {string} id - ID of the recipe to update
 * @param {FormData} data - FormData object containing updated fields and images
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated recipe data
 */
export const updateRecipe = async (id, data, token) => {
    try {
        const response = await apiClient.put(`/recipes/${id}`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error updating recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Fork a recipe
 * @param {string} id - ID of the recipe to fork
 * @param {Object} data - JSON object containing fork details
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Forked recipe data
 */
export const forkRecipe = async (id, data, token) => {
    try {
        const response = await api.post(`/recipes/${id}/fork`, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error forking recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Import a recipe from a URL
 * @param {string} url - URL of the recipe to import
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Imported recipe data
 */
export const importRecipeFromUrl = async (url, token) => {
    try {
        // Using the standard `api` instance since we're sending JSON
        const response = await api.post('/recipes/convert-url', { url }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error importing recipe from URL:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get a recipe by ID
 * @param {string} id - ID of the recipe to fetch
 * @returns {Promise<Object>} - Recipe data
 */
export const getRecipeById = async (id) => {
    try {
        const response = await api.get(`/recipes/${id}`);
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching recipe by ID:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all recipes
 * @returns {Promise<Array>} - Array of all recipes
 */
export const getAllRecipes = async () => {
    try {
        const response = await api.get('/recipes');
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching all recipes:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get all recipes for a specific user
 * @param {string} userId - ID of the user
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} - Array of user's recipes
 */
export const getUserRecipes = async (userId, token) => {
    try {
        const response = await api.get(`/users/${userId}/recipes`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user recipes:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Delete a recipe
 * @param {string} id - ID of the recipe to delete
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Deletion confirmation
 */
export const deleteRecipe = async (id, token) => {
    try {
        const response = await apiClient.delete(`/recipes/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error deleting recipe:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Get the profile of the logged-in user
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - User profile data
 */
export const getUserProfile = async (token) => {
    try {
        const response = await api.get('/users/me', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user profile:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Register a new user in the database
 * @param {Object} userData - User registration data
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Registration result
 */
export const registerUser = async (userData, token) => {
    try {
        const response = await api.post('/users/register', userData, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ API: User registration failed', error.response?.data || error);
        
        // Provide specific error information
        const errorMessage = error.response?.data?.message || error.message || 'Database registration failed';
        const errorDetails = {
            message: errorMessage,
            status: error.response?.status,
            code: error.response?.data?.code
        };
        
        // Throw a more descriptive error
        const enhancedError = new Error(`Database registration failed: ${errorMessage}`);
        enhancedError.details = errorDetails;
        throw enhancedError;
    }
};

/**
 * Get a user by ID
 * @param {string} userId - ID of the user to fetch
 * @returns {Promise<Object>} - User data
 */
export const getUserById = async (userId) => {
    try {
        const response = await api.get(`/users/${userId}`);
        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user by ID:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Update user profile
 * @param {string} userId - ID of the user to update
 * @param {Object} profileData - Profile data to update
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Updated user data
 */
export const updateUserProfile = async (userId, profileData, token) => {
    try {
        // Send all the profile data including new fields
        const body = { 
            display_name: profileData.display_name || profileData.displayName || '',
            is_public: profileData.is_public || false,
            background_info: profileData.background_info || {}
        };
        
        const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(body),
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Server error');
        }
        return await response.json();
    } catch (error) {
        console.error('❌ Error updating user profile:', error);
        throw error;
    }
};

/**
 * Health Check
 * @returns {Promise<Object>} - Backend health status
 */
export const healthCheck = async () => {
    try {
        const response = await api.get('/health');
        return response.data;
    } catch (error) {
        console.error('❌ Error during health check:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate recipe with AI context using both user input and URL data
 * @param {Object} data - Object containing user recipe data and URL
 * @param {string} token - Authorization token
 * @returns {Promise<Object>} - Generated recipe data
 */
export const generateRecipeWithAI = async (data, token) => {
    try {
        const response = await api.post('/recipes/generate-with-context', data, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ Error generating recipe with AI:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate AI analysis for a recipe including flavor profile, difficulty, and user compatibility
 * @param {string} recipeId - ID of the recipe to analyze
 * @param {string} token - Authorization token (optional, for personalized analysis)
 * @returns {Promise<Object>} - AI analysis data
 */
export const generateRecipeAnalysis = async (recipeId, token = null) => {
    try {
        const headers = {
            'Content-Type': 'application/json',
        };
        
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await api.post(`/recipes/${recipeId}/analyze`, {}, {
            headers,
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ Error generating recipe analysis:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};

/**
 * Generate personalized recipe summary based on user preferences and dietary restrictions
 * @param {string} recipeId - ID of the recipe to summarize
 * @param {string} token - Authorization token (required for personalized summary)
 * @returns {Promise<Object>} - Personalized summary data
 */
export const generatePersonalizedSummary = async (recipeId, token) => {
    try {
        const response = await api.post(`/recipes/${recipeId}/personalized-summary`, {}, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });
        
        return response.data;
    } catch (error) {
        console.error('❌ Error generating personalized summary:', error.response?.data?.message || error.message);
        throw error.response?.data || { message: error.message };
    }
};